import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ProviderAccountBlock } from '../integrations/connection/account';
import { ObjectTabs, SettingsLink } from '../../components/ObjectTabs';
import { hubUrl } from '../../nav/hubs';
import { EppPage } from './EppPage';
import { PollPage } from './PollPage';
import { ReconciliationPage } from './ReconciliationPage';
import { SessionsPage } from './SessionsPage';
import { TransactionsPage } from './TransactionsPage';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../../api';
import type { RegistryCertificateStatus, RegistryCredentialStatus, RegistryEnvironmentStatus, Tld } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { daysUntil, formatCount, formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  AdapterLabel,
  Capabilities,
  CountBadge,
  CREDENTIAL_STATUS,
  EnumBadge,
  enumLabel,
  HealthTiles,
  IssueTable,
  PollTable,
  REGISTRY_STATUS,
  RefreshButton,
  TldRef,
  TransactionTable,
  useRegistryCrumbs,
  When,
} from './shared';

/** Login versions of the connection: version, state and whether a password is stored. */
function Logins({ rows }: { rows: RegistryCredentialStatus[] }) {
  const { t } = useTranslation('registry');
  if (rows.length === 0) return <Badge tone="crit">{t('registryPage.noLogin')}</Badge>;
  return (
    <ul className="space-y-1">
      {rows.map((c) => (
        <li key={c.version} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">{t('registryPage.version', { n: c.version })}</span>
          <EnumBadge group="credentialStatus" map={CREDENTIAL_STATUS} value={c.status} />
          {c.password_configured ? <Badge tone="ok">{t('registryPage.passwordStored')}</Badge> : <Badge tone="crit">{t('registryPage.passwordMissing')}</Badge>}
        </li>
      ))}
    </ul>
  );
}

/** Certificate versions of the connection: validity and whether the private key is stored. */
function Certificates({ rows }: { rows: RegistryCertificateStatus[] }) {
  const { t } = useTranslation('registry');
  if (rows.length === 0) return <span className="text-xs text-muted-foreground">{t('registryPage.noCertificate')}</span>;
  return (
    <ul className="space-y-1">
      {rows.map((c) => {
        const left = daysUntil(c.not_after);
        return (
          <li key={c.version} className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">{t('registryPage.version', { n: c.version })}</span>
            <EnumBadge group="credentialStatus" map={CREDENTIAL_STATUS} value={c.status} />
            <span className="whitespace-nowrap">{t('registryPage.validity', { from: formatDate(c.not_before), to: formatDate(c.not_after) })}</span>
            {c.status === 'ACTIVE' && left < 0 ? <Badge tone="crit">{t('registryPage.expired')}</Badge> : null}
            {c.status === 'ACTIVE' && left >= 0 && left <= 30 ? <Badge tone="warn">{t('registryPage.expiresSoon', { count: left })}</Badge> : null}
            {c.private_key_configured ? <Badge tone="ok">{t('registryPage.keyStored')}</Badge> : <Badge tone="crit">{t('registryPage.keyMissing')}</Badge>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Where this registry's connection is configured (Settings › Connections): its own connection
 * page when one has the registry's code, the connection list otherwise. `ote` points at the
 * connection's OT&E tests instead.
 */
function RegistryConnectionLink({ code, ote = false }: { code: string; ote?: boolean }) {
  const { t } = useTranslation('nav');
  const found = useApi((signal) => api.listConnections({ q: code.toLowerCase() }, { signal }), [code]);
  const hit = found.status === 'success' ? found.data.items.find((c) => c.code === code.toLowerCase()) : undefined;
  const page = hit ? `/settings/registries/${encodeURIComponent(hit.code)}` : hubUrl('settings', 'connections', 'registries');
  if (ote) {
    return <SettingsLink title={t('ui.ote.title')} body={t('ui.registryLink.ote')} to={hit ? `${page}/tests` : hubUrl('settings', 'connections', 'ote')} action={t('ui.registryLink.openTests')} />;
  }
  return <SettingsLink title={t('ui.registryLink.connection')} body={t('ui.registryLink.connectionHint')} to={page} action={t('ui.settingsLink.open')} />;
}

/** `/registry/registries/:code`: one registry with tabs for its connection, sessions, commands, poll queue, reconciliation and OT&E (spec §24–§27). */
export function RegistryDetailPage() {
  const { t } = useTranslation('registry');
  const { code = '' } = useParams();
  const result = useApi((signal) => api.getRegistry(code, { signal }), [code]);
  const name = result.status === 'success' ? result.data.registry.name : code;
  useDocumentTitle(name);
  const crumbs = useRegistryCrumbs({ label: t('registries.title'), to: '/registry/registries' }, { label: name });

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={code} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={code} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('registryPage.notFound') : t('registryPage.loadError')} />
      </>
    );
  }

  const { registry, settings, tlds, recent_transactions, open_issues, poll, poll_messages } = result.data;
  const environments = settings ? [settings] : [];
  const envColumns: Column<RegistryEnvironmentStatus>[] = [
    {
      key: 'endpoint',
      header: t('registryPage.endpoint'),
      cell: (e) => (
        <span className="font-mono text-xs">
          {e.epp_host ?? t('epp.noHost')}:{e.epp_port ?? '—'}
        </span>
      ),
    },
    { key: 'active', header: t('registryPage.inUse'), cell: (e) => (e.active ? <Badge tone="ok">{t('epp.active')}</Badge> : <Badge tone="neutral">{t('epp.inactive')}</Badge>) },
    { key: 'login', header: t('registryPage.login'), cell: (e) => <Logins rows={e.credentials} /> },
    { key: 'cert', header: t('registryPage.certificate'), cell: (e) => <Certificates rows={e.certificates} /> },
  ];
  const zoneColumns: Column<Tld>[] = [
    { key: 'zone', header: t('tlds.zone'), cell: (z) => <TldRef zone={z.zone} id={z.id} /> },
    { key: 'class', header: t('tlds.class'), cell: (z) => <span title={z.tld_class}>{enumLabel('tldClass', z.tld_class)}</span> },
    { key: 'dossier', header: t('tlds.dossier'), cell: (z) => <span className="text-xs" title={z.dossier_type}>{enumLabel('dossierType', z.dossier_type)}</span> },
    { key: 'status', header: t('tlds.status'), cell: (z) => <EnumBadge group="registryStatus" map={REGISTRY_STATUS} value={z.status} /> },
    { key: 'managed', header: t('tlds.managed'), numeric: true, cell: (z) => formatCount(z.managed_domains) },
  ];

  const overview = (
      <div className="space-y-6">
      <HealthTiles r={registry} />

      <Section title={t('registryPage.overview')}>
        <div className="space-y-5">
          <KeyValue
            columns={3}
            rows={[
              [t('registryPage.code'), <code key="c" className="text-xs">{registry.code}</code>],
              [t('registries.adapter'), <AdapterLabel key="a" adapter={registry.adapter} />],
              [t('registryPage.status'), <EnumBadge key="s" group="registryStatus" map={REGISTRY_STATUS} value={registry.status} />],
              [t('registryPage.created'), formatDateTime(registry.created_at)],
              [t('registryPage.updated'), formatDateTime(registry.updated_at)],
              [t('registryPage.id'), <CopyText key="id" value={registry.id} />],
            ]}
          />
          <div className="border-t pt-4">
            <Capabilities c={registry.capabilities} />
          </div>
        </div>
      </Section>

      <Section
        flush
        title={t('registryPage.environments')}
        description={t('registryPage.secretsNote')}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={`/registry/registries/${encodeURIComponent(registry.code)}?tab=connection`}>
              {t('registryPage.openEpp')} <ArrowRight />
            </Link>
          </Button>
        }
      >
        <DataTable columns={envColumns} rows={environments} rowKey={(e) => e.id} empty={<EmptyState title={t('registryPage.noEnvironments')} />} />
      </Section>

      <ProviderAccountBlock connectionId={registry.id} />

      <Section flush title={t('registryPage.zones')}>
        <DataTable columns={zoneColumns} rows={tlds} rowKey={(z) => z.id} empty={<EmptyState title={t('registryPage.noZones')} />} />
      </Section>

      <Section flush title={t('registryPage.recentCommands')}>
        {recent_transactions.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState title={t('registryPage.noCommands')} />
          </div>
        ) : (
          <TransactionTable rows={recent_transactions} showRegistry={false} />
        )}
      </Section>

      <Section flush title={t('registryPage.openIssues')}>
        <IssueTable rows={open_issues} compact empty={<EmptyState title={t('registryPage.noOpenIssues')} />} />
      </Section>

      <Section flush title={t('registryPage.pollQueue')}>
        {poll ? (
          <div className="px-5 pb-4">
            <KeyValue
              columns={3}
              rows={[
                [t('poll.receivedCount'), formatCount(poll.total)],
                [t('poll.unprocessed'), <CountBadge key="u" n={poll.unprocessed} tone="warn" />],
                [t('poll.unacked'), <CountBadge key="a" n={poll.unacked} tone="warn" />],
                [t('poll.errors'), <CountBadge key="e" n={poll.errors} tone="crit" />],
                [t('poll.oldest'), <When key="o" at={poll.oldest_unprocessed_at} />],
                [t('poll.lastReceived'), <When key="l" at={poll.last_received_at} />],
              ]}
            />
          </div>
        ) : null}
        <PollTable rows={poll_messages} compact empty={<EmptyState title={t('registryPage.noPollMessages')} />} />
      </Section>
    </div>
  );

  return (
    <>
      <PageHeader
        title={registry.name}
        crumbs={crumbs}
        subtitle={t('registryPage.subtitle')}
        actions={
          <>
            <EnumBadge group="registryStatus" map={REGISTRY_STATUS} value={registry.status} />
            <RefreshButton onClick={result.reload} />
          </>
        }
      />
      <ObjectTabs
        base={`/registry/registries/${encodeURIComponent(registry.code)}`}
        label={registry.name}
        tabs={[
          { id: 'overview', label: t('registryPage.tab.overview'), content: overview },
          {
            id: 'connection',
            label: t('registryPage.tab.connection'),
            embedded: true,
            fixed: { registry: registry.code },
            content: (
              <>
                <RegistryConnectionLink code={registry.code} />
                <EppPage />
              </>
            ),
          },
          { id: 'sessions', label: t('registryPage.tab.sessions'), embedded: true, fixed: { registry: registry.code }, content: <SessionsPage /> },
          { id: 'transactions', label: t('registryPage.tab.transactions'), embedded: true, fixed: { registry: registry.code }, content: <TransactionsPage /> },
          { id: 'poll', label: t('registryPage.tab.poll'), embedded: true, fixed: { registry: registry.code }, content: <PollPage /> },
          { id: 'reconciliation', label: t('registryPage.tab.reconciliation'), embedded: true, fixed: { registry: registry.code }, content: <ReconciliationPage /> },
          { id: 'ote', label: t('registryPage.tab.ote'), content: <RegistryConnectionLink code={registry.code} ote /> },
        ]}
      />
    </>
  );
}
